import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AnnotationsModule from "highcharts/modules/annotations";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useGetPayrollStaffDhashboardQuery } from "../../../../Api/attoDeskApi";
import dayjs, { Dayjs } from "dayjs";

AnnotationsModule(Highcharts);

interface IEmployeePayroll {
  payrollId: number;
  payrollGenerateId: string;
  hourly_salary: string | number;
  ot_hourly_salary: string | number;
  bonus_amount: string | number;
  startDate: string;
  endDate: string;
}

interface IProductPriceSplineChartRecord {
  staffId: string;
  selectedStartDate: Dayjs;
  selectedEndDate: Dayjs;
}

const ProductPriceSplineChart: React.FC<IProductPriceSplineChartRecord> = ({
  staffId,
  selectedStartDate,
  selectedEndDate,
}) => {
  const theme = useTheme();
  const [showTable, setShowTable] = useState(false);

  const { data: payrollApiData } = useGetPayrollStaffDhashboardQuery({
    empid: staffId,
    startDate: selectedStartDate.format("YYYY-MM-DD"),
      endDate: selectedEndDate.format("YYYY-MM-DD"),
  });

  // ✅ Ensure payrollData is typed as array
  // const payrollData: IEmployeePayroll[] = useMemo(() => {
  //   return Array.isArray(payrollApiData?.data ?? [])
  //     ? (payrollApiData.data as IEmployeePayroll[])
  //     : [];
  // }, [payrollApiData?.data]);

  const payrollData = useMemo(() => {
    return (payrollApiData?.data as IEmployeePayroll[]) || [];
  }, [payrollApiData?.data]);

  // Build series
  const parsedSeries: Highcharts.SeriesSplineOptions[] = [
    {
      type: "spline",
      name: "Hourly Salary",
      data: payrollData.map((item: IEmployeePayroll) => [
        item.payrollGenerateId,
        Number(item.hourly_salary),
        item.startDate,
        item.endDate,
      ]),
    },
    {
      type: "spline",
      name: "OT Hourly Salary",
      data: payrollData.map((item: IEmployeePayroll) => [
        item.payrollGenerateId,
        Number(item.ot_hourly_salary),
        item.startDate,
        item.endDate,
      ]),
    },
    {
      type: "spline",
      name: "Bonus Amount",
      data: payrollData.map((item: IEmployeePayroll) => [
        item.payrollGenerateId,
        Number(item.bonus_amount),
        item.startDate,
        item.endDate,
      ]),
    },
  ];

  // Table rows
  const tableRows = payrollData.map((item: IEmployeePayroll) => ({
    payrollGenerateId: item.payrollGenerateId,
    "Hourly Salary": Number(item.hourly_salary),
    "OT Hourly Salary": Number(item.ot_hourly_salary),
    "Bonus Amount": Number(item.bonus_amount),
  }));

  const options: Highcharts.Options = {
    chart: {
      type: "spline",
      backgroundColor: theme.palette.cardAdd.bgColor,
    },
    title: {
      text: "Employee Salary Trends",
      style: {
        fontSize: "0.9375rem",
        color: theme.palette.heading.headingColor,
      },
    },
    xAxis: {
      categories: payrollData.map(
        (item: IEmployeePayroll) => item.payrollGenerateId
      ),
      title: {
        text: "Payroll",
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      labels: {
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
    },
    yAxis: {
      title: {
        text: "Amount ($)",
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      labels: {
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
    },
    tooltip: {
      shared: true,
      formatter: function () {
        return (
          `<b>Payroll: ${this.x}</b><br/>` +
          this.points
            ?.map((p) => `${p.series.name}: $${Number(p.y).toFixed(2)}`)
            .join("<br/>")
        );
      },
    },
    // tooltip: {
    //   shared: true,
    //   useHTML: true,
    //   formatter: function () {
    //     if (!this.points) return "";

    //     // Assuming each point has custom startDate and endDate in point.options
    //     const startDate = dayjs(this.points[0].point.startDate).format(
    //       "MMM-DD, YYYY"
    //     );
    //     const endDate = dayjs(this.points[0].point.endDate).format(
    //       "MMM-DD, YYYY"
    //     );

    //     return (
    //       `<b>Payroll: ${this.x}</b><br/>` +
    //       `<b>Date Range: ${startDate} - ${endDate}</b><br/>` +
    //       this.points
    //         .map((p) => `${p.series.name}: $${Number(p.y).toFixed(2)}`)
    //         .join("<br/>")
    //     );
    //   },
    // },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: {
        fontSize: "0.75rem",
        color: theme.palette.heading.headingColor,
      },
    },
    plotOptions: {
      spline: { marker: { enabled: true } },
    },
    series: parsedSeries,
  };

  return (
    <Box>
      {payrollData.length > 0 ? (
        <>
          <HighchartsReact highcharts={Highcharts} options={options} />

          <Typography
            onClick={() => setShowTable(!showTable)}
            sx={{ cursor: "pointer", textAlign: "center", mt: 1 }}
            variant="h6"
          >
            {showTable ? "Hide Table View" : "View Table"}
          </Typography>
        </>
      ) : (
        <>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </>
      )}

      {showTable && (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Payroll</strong>
                </TableCell>
                {parsedSeries.map((series, idx) => (
                  <TableCell key={idx}>
                    <strong>{series.name}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {tableRows.map((row: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Typography>
                      {`${row.payrollGenerateId} | ${dayjs(
                        row.startDate
                      ).format("MMM-DD, YYYY")} - ${dayjs(row.endDate).format(
                        "MMM-DD, YYYY"
                      )}`}
                    </Typography>
                  </TableCell>

                  {parsedSeries.map((series, sIdx) => {
                    const key = series.name ?? "";
                    return (
                      <TableCell key={sIdx}>
                        {row[key] !== undefined
                          ? Number(row[key]).toFixed(2)
                          : "-"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProductPriceSplineChart;
